import { Link, useNavigate } from "react-router";
import { useReportHistory, useDeleteReport } from "../../hooks/useReportHistory";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Trash2, FileText, Settings } from "lucide-react";
import DarkModeToggle from "../../components/DarkModeToggle";
import { usePollTask } from "@/src/hooks/usePollTask";
import { useTrackPageView } from "../../hooks/useTrackPageView";

function ReportRow({ report }) {
  const navigate = useNavigate();
  const deleteReport = useDeleteReport();
  usePollTask(report.id, report.status !== "complete" && report.status !== "failed");

  const handleDelete = (id, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (window.confirm("Delete this report?")) {
      deleteReport.mutate(id);
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "requested":
        return "secondary";
      case "building":
        return "default";
      case "complete":
        return "success";
      case "failed":
        return "destructive";
      default:
        return "outline";
    }
  };
  return (
    <TableRow
      key={report.id}
      className="cursor-pointer"
      onClick={() => navigate(`/v1/report/${report.id}`)}
    >
      <TableCell className="font-medium max-w-0">
        <div className="truncate pr-4">{report.title || report.request}</div>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {new Date(report.timestamp).toLocaleString()}
      </TableCell>
      <TableCell>
        <Badge variant={getStatusVariant(report.status)}>{report.status?.replace(/_/g, " ")}</Badge>
      </TableCell>
      <TableCell className="text-right">
        <Button
          onClick={(e) => handleDelete(report.id, e)}
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          aria-label="Delete report"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function Reports() {
  useTrackPageView("Reports");
  const { data: reports = [], isLoading } = useReportHistory();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
          <Link to="/v1/settings">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
            >
              <Settings className="h-4 w-4" />
              <span className="sr-only">Settings</span>
            </Button>
          </Link>
          <div className="h-5 w-px bg-border" />
          <DarkModeToggle />
        </div>
        <div className="container mx-auto p-8 pt-20">
          <div className="text-center text-muted-foreground">Loading reports...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Settings and Dark mode toggle - top left */}
      <div className="absolute top-0 left-0 p-6 flex items-center gap-2">
        <Link to="/v1/settings">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
          >
            <Settings className="h-4 w-4" />
            <span className="sr-only">Settings</span>
          </Button>
        </Link>
        <div className="h-5 w-px bg-border" />
        <DarkModeToggle />
      </div>

      {/* Navigation - top right */}
      <nav className="absolute top-0 right-0 p-6">
        <Button asChild>
          <Link to="/v1/">New Report</Link>
        </Button>
      </nav>

      <div className="container mx-auto p-8 max-w-6xl pt-20">
        <div className="mb-8">
          <h1 className="text-3xl font-bold">Report History</h1>
        </div>

        {reports.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No reports yet</p>
            <Button asChild>
              <Link to="/v1/">Create your first report</Link>
            </Button>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50%]">Report</TableHead>
                  <TableHead className="w-[25%]">Date</TableHead>
                  <TableHead className="w-[15%]">Status</TableHead>
                  <TableHead className="w-[10%] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reports.map((report) => (
                  <ReportRow report={report} key={report.id} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Reports;
